import type { PoolRpcResponse, TokenSwapGetFarmedParams, TokenSwapLoadParams } from "@aldrin_exchange/sdk";
import {
  DEFAULT_FARMING_TICKET_END_TIME,
  FarmingClient,
  PoolClient,
  SOLANA_RPC_ENDPOINT,
  TokenClient,
} from "@aldrin_exchange/sdk";
import type { Wallet } from "@aldrin_exchange/sdk/dist/types/types";
import { Connection } from "@solana/web3.js";
import BigNumber from "bignumber.js";

export class TokenSwap {
  constructor(
    private pools: PoolRpcResponse[],
    private poolClient: PoolClient,
    private tokenClient: TokenClient,
    private farmingClient: FarmingClient,
    private connection = new Connection(SOLANA_RPC_ENDPOINT),
    private wallet: Wallet | null = null,
  ) {}

  static async initialize(params: TokenSwapLoadParams = {}): Promise<TokenSwap> {
    const { connection = new Connection(SOLANA_RPC_ENDPOINT), wallet } = params;
    const poolClient = new PoolClient(connection);
    const tokenClient = new TokenClient(connection);
    const farmingClient = new FarmingClient(connection);

    const [pools, v2Pools] = await Promise.all([poolClient.getPools(), poolClient.getV2Pools()]);

    return new TokenSwap([...pools, ...v2Pools], poolClient, tokenClient, farmingClient, connection, wallet);
  }

  async getFarmed(params: TokenSwapGetFarmedParams) {
    const { poolMint, wallet = this.wallet } = params;

    if (!wallet) {
      throw new Error("Wallet not provided");
    }

    const pool = this.pools.find((p) => p.poolMint.equals(poolMint));

    if (!pool) {
      throw new Error("Pool not found!");
    }

    const tickets = await this.farmingClient.getFarmingTickets({
      userKey: wallet.publicKey,
      pool: pool.poolPublicKey,
      poolVersion: pool.poolVersion,
    });

    if (tickets.length === 0) {
      throw new Error("No tickets, nothing to check");
    }

    const states = await this.farmingClient.getFarmingState({
      poolPublicKey: pool.poolPublicKey,
      poolVersion: pool.poolVersion,
    });

    const stateKeys = states.map((state) => state.farmingStatePublicKey.toBase58());

    // Resolve rewards
    const stateVaults = await Promise.all(
      states.map(async (state) => {
        const tokenInfo = await this.tokenClient.getTokenAccount(state.farmingTokenVault);

        return {
          tokenInfo,
          state,
        };
      }),
    );

    const calcAccounts = await this.farmingClient.getFarmingCalcAccounts({
      poolVersion: pool.poolVersion,
      userKey: wallet.publicKey,
    });

    const calcAccountsForStates = calcAccounts.filter((ca) => stateKeys.includes(ca.farmingState.toBase58()));

    const userStakedAmount = tickets.reduce((acc, ticket) => {
      if (ticket.endTime.eq(DEFAULT_FARMING_TICKET_END_TIME)) {
        acc = acc.plus(ticket.tokensFrozen.toString());
      }
      return acc;
    }, new BigNumber(0));

    return stateVaults
      .map((sv) => {
        const calcAccount = calcAccountsForStates.find((ca) => ca.farmingState.equals(sv.state.farmingStatePublicKey));

        return { ...sv, calcAccount, userStakedAmount };
      })
      .filter((sv) => sv.calcAccount?.tokenAmount.gtn(0));
  }
}
